import React from "react";
import PropTypes from "prop-types";
import styles from "./styles.styl";

/**
 * A customizable text component.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {string} [props.variant="body"] - The variant of the text (h1, h2, h3, body, caption).
 * @param {string} [props.weight="normal"] - The weight of the text (normal, medium, bold).
 * @param {string} props.color - The color of the text.
 * @param {string} [props.className=""] - Additional CSS class names for the text.
 * @param {string} [props.as="span"] - The HTML element to render (span or label).
 * @param {React.ReactNode} props.children - The content of the text component.
 * @returns {JSX.Element} The rendered text component.
 */
export const Text = ({
	variant = "body",
	weight = "normal",
	color,
	className = "",
	as = "span",
	children,
	...rest
}) => {
	const classes = [styles.text, styles[`text--${variant}`], styles[`text--${weight}`], className]
		.filter(Boolean)
		.join(" ");

	const style = color ? { color } : undefined;

	const Element = as;

	return (
		<Element className={classes} style={style} {...rest}>
			{children}
		</Element>
	);
};

Text.propTypes = {
	variant: PropTypes.oneOf(["h1", "h2", "h3", "body", "caption"]),
	weight: PropTypes.oneOf(["normal", "medium", "bold"]),
	color: PropTypes.string,
	className: PropTypes.string,
	as: PropTypes.oneOf(["span", "label"]),
	children: PropTypes.node.isRequired,
};
